import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueue } from "@/context/QueueContext";
import { apiCall } from "@/api/apiCall";
import DottedLineBarChart from "../components/charts/DottedLineBarChart";
import LoadingComponent from "@/components/common/LoadingComponent";
import HeadingComponent from "../components/shared/heading";
import ErrorState from "@/components/common/ErrorStateComponent";
import { processParams } from "../utils/paramsHelper";
import AiCfoSection from "../components/shared/AiCfoSection";

// ─── Data Transformer ───────────────────────────────────────────────────────
function transformApiResponse(apiData) {
  const d = apiData?.data ?? {};
  const labels = d.headerYears || [];
  const getRow = (name) =>
    d.rows?.find((r) => r.label === name)?.values || [];

  const parseNumber = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "string") {
    // ✅ Handle (123,456) format — parentheses = negative
    const isNegative = v.trim().startsWith("(");
    const cleaned = v.replace(/[(),\s]/g, "").replace("%", "");
    const num = Number(cleaned);
    return isNaN(num) ? 0 : isNegative ? -num : num;
  }
  return typeof v === "number" ? v : 0;
};

  return {
    labels,
    totalRevenue: getRow("Total Revenue").map(parseNumber),
    ebitda: getRow("EBITDA").map(parseNumber),
    opProfit: getRow("OPERATING PROFIT/(LOSS)").map(parseNumber),
    ebitdaMargin: getRow("EBITDA Margin").map(parseNumber),
    netProfitMargin: getRow("NET PROFIT MARGIN").map(parseNumber),
  };
}

// ─── Helper to resolve placeholders ─────────────────────────────────────────
function resolveString(str, runtimeValues = {}) {
  if (typeof str !== "string") return "";
  return str.replace(/\{\{(\w+)\}\}/g, (_, k) =>
    runtimeValues[k] !== undefined ? runtimeValues[k] : `{{${k}}}`
  );
}

export default function PageOneChartContainer({
  year = "",
  month = "",
  monthNumber = "",
  contract = "",
  page = null,
  config = {},
  isExporting = false, // ✅ export mode
  cfoComment = "", // ✅ lifted state from BoardPack
  onCfoCommentChange, // ✅ setter from BoardPack
  isRefresh = false 
}) {
  const navigate = useNavigate();
  const { enqueue } = useQueue();
  const hasFetchedRef = useRef(false);

   const hasComment = cfoComment.trim() !== "";
  const [chartData, setChartData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const runtimeValues = { year, month, contract };
  const is_cfo = page?.params?.is_cfo ? true : false;

  const chartTitle = resolveString(
    page?.in_chart_content_line_1 || page?.title || "",
    runtimeValues
  );

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiParams = processParams(page?.params || {}, { year, month, contract });

      const response = await enqueue(() =>
        apiCall(
          "GET",
          page?.api,
          apiParams,
          navigate,
                false,
                false,
                isRefresh
        )
      );

      if (response) {
        setChartData(transformApiResponse(response));
        setSummary({ lines: response?.lines || [] });
      } else {
        setError("No data received from server.");
      }
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to load chart data.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingComponent />;

  if (error) {
    return (
     <ErrorState error={error} onRetry={fetchData}/>
    );
  }

  return (
    // ⬇ min-h-screen hata diya — BoardPackLayout ka canvas already fixed
    // (720px) hai, min-h-screen sirf extra height force karta tha jo
    // fixed-height parent ke andar top se overflow/cut ka baais ban raha tha.
    <div className="w-full py-1 px-2">
      <div
        className="w-full mx-auto px-4 py-2 rounded-lg shadow-lg"
        style={{ backgroundColor: "#ffffff" }}
      >
        {/* Title & Subtitle */}
        <div
          className="text-center"
          style={{
            backgroundColor: "#ffffff",
            padding: "4px 0 0 0",
            margin: "0 0 4px 0",
          }}
        >
          {page?.title && (
            <HeadingComponent variant={"main"}>
              {resolveString(page.title, runtimeValues)}
            </HeadingComponent>
          )}
          {page?.subtitle && (
            <HeadingComponent variant={"sub"}>
              {resolveString(page.subtitle, runtimeValues)}
            </HeadingComponent>
          )}
        </div>

        {/* Chart */}
        <DottedLineBarChart
          labels={chartData.labels}
          totalRevenue={chartData.totalRevenue}
          ebitda={chartData.ebitda}
          opProfit={chartData.opProfit}
          ebitdaMargin={chartData.ebitdaMargin}
          netProfitMargin={chartData.netProfitMargin}
          title={chartTitle}
          isCfo={is_cfo}
        />
         <AiCfoSection
  isCfo={is_cfo}
  summary={summary}
  cfoComment={cfoComment}
  onCfoCommentChange={onCfoCommentChange}
  isExporting={isExporting}
  compact={true}
/>
      </div>
    </div>
  );
}